import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, KpiCard, SectionCard, StatusPill, EmptyState } from "../../components/Common";
import { BedDouble, Search, AlertTriangle, Stethoscope, Activity, Lock } from "lucide-react";
import { StatusChip, WARDS, STATUSES } from "./parts";
import { AdmissionDetail } from "./AdmissionDetail";
import { BedBoardStrip } from "./BedBoardStrip";
import { DoctorAvailabilityDot } from "./DoctorAvailabilityDot";

export default function AdmittedList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [ward, setWard] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showDischarged, setShowDischarged] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [bedRefreshKey, setBedRefreshKey] = useState(0);

  const load = async () => {
    try {
      setLoading(true);
      const reqs = [api.get("/admissions?status=Admitted")];
      if (showDischarged) reqs.push(api.get("/admissions?status=Discharged"));
      const results = await Promise.all(reqs);
      const merged = results.flatMap((r) => r.data || []);
      setItems(merged);
      setBedRefreshKey((k) => k + 1);
    } catch { toast.error("Failed to load admissions"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [showDischarged]); // eslint-disable-line

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((a) => {
      if (ward !== "All" && a.ward !== ward) return false;
      if (statusFilter !== "All" && (a.clinical_status || "Not set") !== statusFilter) return false;
      if (!q) return true;
      return [a.patient_name, a.patient_ths_id, a.admission_no, a.admitting_diagnosis, a.admitting_doctor, a.bed_no]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [items, query, ward, statusFilter]);

  const activeItems = items.filter((a) => a.status === "Admitted");
  const criticalCount = activeItems.filter((a) => a.clinical_status === "Critical").length;
  const observationCount = activeItems.filter((a) => a.clinical_status === "Under observation").length;
  const totalActive = activeItems.length;

  if (selectedId) return <AdmissionDetail admissionId={selectedId} onBack={() => setSelectedId(null)} onChanged={load} />;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader title="Admitted Patients" subtitle="Current inpatient admissions, medications, and ward round notes." />

      {criticalCount > 0 && (
        <div className="rounded-xl bg-[#fdecec] border border-[#f5c2c2] p-4 flex items-start gap-3" data-testid="critical-adm-alert">
          <AlertTriangle className="w-5 h-5 text-[#B71C1C] mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-[#B71C1C]">{criticalCount} critical inpatient{criticalCount > 1 ? "s require" : " requires"} immediate attention</div>
            <div className="text-sm text-[#B71C1C]/80 mt-1">Filter by status “Critical” below to review.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-admitted-total" label="Active admissions" value={totalActive} icon={BedDouble} tone="blue" />
        <KpiCard testId="kpi-admitted-critical" label="Critical" value={criticalCount} icon={AlertTriangle} tone="red" hint="Clinical status = Critical" />
        <KpiCard testId="kpi-admitted-observation" label="Under observation" value={observationCount} icon={Activity} tone="amber" />
        <KpiCard testId="kpi-admitted-wards" label="Wards in use" value={new Set(activeItems.map(i => i.ward)).size} icon={Stethoscope} tone="slate" />
      </div>

      <BedBoardStrip refreshKey={bedRefreshKey} />

      <SectionCard title={`Admissions (${filtered.length})`} testId="admissions-list-card" actions={
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input type="checkbox" data-testid="adm-show-discharged" checked={showDischarged} onChange={(e) => setShowDischarged(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#1976D2] focus:ring-[#1976D2]" />
            Show discharged
          </label>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="adm-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Patient, Patient ID, ward, bed, diagnosis…" className="h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]" />
          </div>
          <select data-testid="adm-ward-filter" value={ward} onChange={(e) => setWard(e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            {WARDS.map((w) => <option key={w} value={w}>{w === "All" ? "All wards" : w}</option>)}
          </select>
          <select data-testid="adm-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="All">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            <option value="Not set">Not set</option>
          </select>
        </div>
      }>
        {loading ? (
          <div className="p-6 text-slate-500 text-sm" data-testid="adm-list-loading">Loading admissions…</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={BedDouble} title="No admissions match" description="Adjust filters or admit a patient from the patient chart." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((a) => (
              <li key={a.admission_id}>
                <button data-testid={`adm-row-${a.admission_id}`} onClick={() => setSelectedId(a.admission_id)} className="w-full text-left px-5 py-3 hover:bg-slate-50 flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{a.patient_name}</span>
                      <span className="text-xs text-slate-500 tabular">{a.patient_ths_id}</span>
                      <span className="text-xs text-slate-400">·</span>
                      <span className="text-xs text-slate-500">{a.admission_no}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate flex items-center gap-1.5">
                      <span>{a.admitting_diagnosis} · {a.ward}{a.bed_no ? ` · Bed ${a.bed_no}` : ""} · Admitted by</span>
                      <DoctorAvailabilityDot name={a.admitting_doctor || a.admitted_by_name} />
                      <span>{a.admitting_doctor || a.admitted_by_name}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {a.status === "Discharged" ? (
                      <span className="chip chip-slate flex items-center gap-1 !inline-flex" data-testid={`row-discharged-chip-${a.admission_id}`}><Lock className="w-3 h-3" /> Discharged</span>
                    ) : (
                      <>
                        <StatusChip value={a.clinical_status} />
                        <StatusPill status={a.status} />
                      </>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
